import { Injectable } from '@angular/core';
import { StateService, TransitionService, Transition } from '@uirouter/core';
import { SessionService } from '@edl-development/edl-common';

/**
 * Defines a type of context that can be captured during navigation
 * Each context type knows how to extract itself from a transition
 */
interface ContextDefinition {
    /** Unique identifier for this context type */
    key: string;
    /** UIRouter resolved token to look for */
    resolveToken: string;
    /** Extract and transform the resolved data */
    extract: (data: any) => any;
    /** Priority for label generation (higher = higher priority) */
    priority: number;
}

/**
 * Defines an entry point state
 * Entry points simply declare what context types they provide
 */
interface EntryPointDefinition {
    /** State name pattern to match (uses startsWith) */
    stateName: string;
    /** Which context types this state provides */
    providesContext: string[];
    /** Label to use if no specific context is available */
    fallbackLabel: string;
}

/**
 * Navigation history entry with accumulated context
 */
interface NavigationEntry {
    stateName: string;
    params: any;
    context: NavigationContext;
    timestamp: number;
}

/**
 * Accumulated navigation context
 * Contains all captured context keyed by type
 */
interface NavigationContext {
    [key: string]: any;
}

/**
 * Maps a context type to a fallback destination
 * Used when no history exists (e.g., direct links) to determine where to navigate back to
 */
interface ContextFallbackMapping {
    /** Which context type this applies to (matches contextDefinition key) */
    contextKey: string;
    /** Should this context be used for fallback navigation? */
    shouldUse: (contextData: any) => boolean;
    /** Where to navigate when using this context. Receives context data and current state name. */
    getDestination: (contextData: any, currentStateName?: string) => { stateName: string; params: any };
}

@Injectable({
    providedIn: 'root'
})
export class NavigationHistoryService {
    private history: NavigationEntry[] = [];
    private maxHistorySize = 10;
    private defaultReturnState = 'authenticated.my-items';

    // Priority constants for context label generation (higher = higher priority)
    private readonly PRIORITY_USER = 10;
    private readonly PRIORITY_REQUEST = 8;
    private readonly PRIORITY_COMPLIANCE_ITEM = 7;

    /**
     * Registry of context types that can be captured
     * To add a new context type: add a definition here
     */
    private readonly contextDefinitions: ContextDefinition[] = [
        {
            key: 'user',
            resolveToken: 'user',
            extract: (user: any) => user ? {
                UserID: user.UserID,
                FirstName: user.FirstName,
                LastName: user.LastName
            } : null,
            priority: this.PRIORITY_USER
        },
        {
            key: 'requestItem',
            resolveToken: 'requestItem',
            extract: (item: any) => item ? {
                RequestID: item.RequestID,
                Title: item.Title
            } : null,
            priority: this.PRIORITY_REQUEST
        },
        {
            key: 'selectedRequest',
            resolveToken: 'selectedRequest',
            extract: (request: any) => request ? {
                RequestID: request.RequestID,
                Title: request.Title
            } : null,
            priority: this.PRIORITY_REQUEST
        },
        {
            key: 'complianceItem',
            resolveToken: 'details',
            extract: (details: any) => details?.ComplianceItem ? {
                ComplianceItemID: details.ComplianceItem.ComplianceItemID,
                Title: details.ComplianceItem.Title
            } : null,
            priority: this.PRIORITY_COMPLIANCE_ITEM
        }
    ];

    /**
     * Registry of entry point states
     * To add a new entry point: add a definition here specifying which context it provides
     */
    private readonly entryPointDefinitions: EntryPointDefinition[] = [
        {
            stateName: 'authenticated.my-items.requests',
            providesContext: [],  // Provides no additional context
            fallbackLabel: 'Return to My Items'
        },
        {
            stateName: 'authenticated.my-items.compliance',
            providesContext: [],
            fallbackLabel: 'Return to My Compliance'
        },
        {
            stateName: 'authenticated.all-users-progress.user-compliance.details.compliance',
            providesContext: ['user'],  // This state provides user context
            fallbackLabel: 'Return to User Progress'
        },
        {
            stateName: 'authenticated.all-users-progress.user-compliance.details.requests',
            providesContext: ['user'],  // This state provides user context
            fallbackLabel: 'Return to User Progress'
        },
        {
            stateName: 'authenticated.requests.sm.request',
            providesContext: ['requestItem'],  // This state provides request context
            fallbackLabel: 'Return to Request Management'
        },
        {
            stateName: 'authenticated.requests.sm.search',
            providesContext: [],
            fallbackLabel: 'Return to Request Search'
        }
    ];

    /**
     * Maps context types to fallback destinations
     * Used when no history exists (e.g., direct links) to provide smart navigation
     */
    private readonly contextFallbackMappings: ContextFallbackMapping[] = [
        {
            contextKey: 'user',
            shouldUse: (userData) => {
                // Only use user context if it's NOT the current user
                // This handles "submit on behalf" scenarios
                return userData.UserID !== this.sessionService.user?.UserID;
            },
            getDestination: (userData, currentStateName) => {
                // Determine which tab to return to based on whether current state contains 'request'
                const targetState = currentStateName?.includes('request')
                    ? 'authenticated.all-users-progress.user-compliance.details.requests'
                    : 'authenticated.all-users-progress.user-compliance.details.compliance';

                return {
                    stateName: targetState,
                    params: { userId: userData.UserID, page: 1 }
                };
            }
        },
        {
            contextKey: 'requestItem',
            shouldUse: () => true,  // Always use if available
            getDestination: (requestData) => ({
                stateName: 'authenticated.requests.sm.request',
                params: { requestId: requestData.RequestID }
            })
        },
        {
            contextKey: 'selectedRequest',
            shouldUse: () => true,
            getDestination: (requestData) => ({
                stateName: 'authenticated.requests.sm.request',
                params: { requestId: requestData.RequestID }
            })
        }
    ];

    /**
     * Latest transition - stored to access resolved data for smart fallback
     * When users navigate via direct links (no history), we can still extract
     * context from the current state to determine an appropriate return destination
     */
    private latestTransition: Transition | null = null;

    constructor(
        private stateService: StateService,
        private transitionService: TransitionService,
        private sessionService: SessionService
    ) {
        this.initializeHistoryTracking();
    }

    private initializeHistoryTracking() {
        // Track all successful state transitions
        this.transitionService.onSuccess({}, (transition: Transition) => {
            // Store latest transition for smart fallback access
            this.latestTransition = transition;

            const toState = transition.to();
            const toParams = transition.params('to');
            const stateName = toState.name;

            // Capture context for this transition
            const context = this.captureContextForTransition(transition, stateName);

            this.addToHistory({
                stateName: stateName,
                params: toParams,
                context: context,
                timestamp: Date.now()
            });
 });
    }

    /**
     * Capture context from a transition
     * Automatically looks for all registered context types
     */
    private captureContextForTransition(transition: Transition, stateName: string): NavigationContext {
        const entryPoint = this.findEntryPointDefinition(stateName);
        const context: NavigationContext = {};

        if (!entryPoint) {
            // Not an entry point, return empty context
            return context;
        }

        // For each context type this entry point provides, try to capture it
        for (const contextKey of entryPoint.providesContext) {
            const contextDef = this.contextDefinitions.find(def => def.key === contextKey);
            if (!contextDef) {
                continue;
            }

            // Try to get the resolved data from UIRouter
            const resolvedData = transition.injector().get(contextDef.resolveToken);
            if (resolvedData) {
                const extracted = contextDef.extract(resolvedData);
                if (extracted) {
                    context[contextKey] = extracted;
                }
            }
        }

        return context;
    }

    private addToHistory(entry: NavigationEntry) {
        const lastEntry = this.history[this.history.length - 1];

        // If navigating to the same state (e.g., changing filters/search params),
        // update the existing entry instead of creating a new one
        if (lastEntry && lastEntry.stateName === entry.stateName) {
            lastEntry.params = entry.params;
            lastEntry.timestamp = entry.timestamp;
        } else {
            // Different state - add new entry
            this.history.push(entry);

            // Keep history size manageable
            if (this.history.length > this.maxHistorySize) {
                this.history.shift();
            }
        }
    }

    /**
     * Find the entry point definition for a given state name
     * Uses exact match to ensure only explicitly defined entry points are recognized
     */
    private findEntryPointDefinition(stateName: string): EntryPointDefinition | null {
        const match = this.entryPointDefinitions.find(def => stateName === def.stateName) || null;
        return match;
    }

    /**
     * Returns to the most recent "entry point" state in history
     * Skips over the current state and any intermediate detail views
     * If no history exists (e.g., direct link), uses smart fallback based on available context
     */
    goToLastEntryPoint(): void {
        const currentState = this.stateService.current.name;

        // Find the most recent entry point (excluding current state)
        for (let i = this.history.length - 1; i >= 0; i--) {
            const entry = this.history[i];

            if (entry.stateName !== currentState &&
                this.isEntryPointState(entry.stateName)) {
                this.stateService.go(entry.stateName, entry.params, { reload: true });
                return;
            }
        }

        // No history found - use smart fallback based on available context
        const fallback = this.determineSmartFallback();
        this.stateService.go(fallback.stateName, fallback.params, { reload: true });
    }

    /**
     * Get the most recent entry point state without navigating
     * Returns null if no valid entry point found in history
     */
    getLastEntryPoint(): { stateName: string; params: any } | null {
        const currentState = this.stateService.current.name;

        for (let i = this.history.length - 1; i >= 0; i--) {
            const entry = this.history[i];

            if (entry.stateName !== currentState &&
                this.isEntryPointState(entry.stateName)) {
                return { stateName: entry.stateName, params: entry.params };
            }
        }

        return null;
    }

    /**
     * Check if a given state name is considered an entry point
     */
    private isEntryPointState(stateName: string): boolean {
        return this.findEntryPointDefinition(stateName) !== null;
    }

    /**
     * Get a user-friendly label for the return destination
     * Uses captured context from the last entry point to generate an appropriate label
     * If no history exists (e.g., direct link), uses smart fallback based on current context
     * This method is parameter-free - context is automatically captured on navigation
     */
    getReturnLabel(): string {
        const entryPoint = this.getLastEntryPointWithContext();

        if (entryPoint) {
            // Have history - use captured context from entry point
            const entryDef = this.findEntryPointDefinition(entryPoint.stateName);
            if (!entryDef) {
                return 'Return';
            }
            return this.generateLabelFromContext(entryPoint.context, entryDef.fallbackLabel);
        }

        // No history - generate label from current state's context
        const currentContext = this.extractContextFromCurrentState();
        return this.generateLabelFromContext(currentContext, 'Return to My Items');
    }

    /**
     * Generate a return label based on captured context
     * Uses priority ordering to determine which context to prioritize
     */
    private generateLabelFromContext(context: NavigationContext, fallbackLabel: string): string {
        // Sort context definitions by priority (highest first)
        const sortedDefs = [...this.contextDefinitions].sort((a, b) => b.priority - a.priority);

        // Try each context type in priority order
        for (const def of sortedDefs) {
            const contextData = context[def.key];
            if (!contextData) {
                continue;
            }

            // Generate label for this context type
            const label = this.generateLabelForContextType(def.key, contextData);
            if (label) {
                return label;
            }
        }

        // No context available, use fallback
        return fallbackLabel;
    }

    /**
     * Generate label for a specific context type
     * Centralized label generation logic
     */
    private generateLabelForContextType(contextKey: string, contextData: any): string | null {
        switch (contextKey) {
            case 'user':
                if (contextData.FirstName && contextData.LastName) {
                    return `Return to ${contextData.LastName}, ${contextData.FirstName}`;
                }
                return null;

            case 'requestItem':
            case 'selectedRequest':
                if (contextData.Title) {
                    return `Return to ${contextData.Title}`;
                }
                return null;

            case 'complianceItem':
                if (contextData.Title) {
                    return `Return to ${contextData.Title}`;
                }
                return null;

            default:
                return null;
        }
    }

    /**
     * Get the most recent entry point with its captured context
     * Returns null if no valid entry point found in history
     */
    private getLastEntryPointWithContext(): NavigationEntry | null {
        const currentState = this.stateService.current.name;

        for (let i = this.history.length - 1; i >= 0; i--) {
            const entry = this.history[i];

            if (entry.stateName !== currentState &&
                this.isEntryPointState(entry.stateName)) {
                return entry;
            }
        }

        return null;
    }

    /**
     * Extract context from the current state using existing contextDefinitions
     * This reuses the same extraction logic used when entering entry points
     * Useful for determining smart fallback when no history exists (e.g., direct links)
     */
    private extractContextFromCurrentState(): NavigationContext {
        const context: NavigationContext = {};

        if (!this.latestTransition) {
            return context;
        }

        // Use existing contextDefinitions to extract context from current state
        for (const contextDef of this.contextDefinitions) {
            try {
                const resolvedData = this.latestTransition.injector().get(contextDef.resolveToken);
                if (resolvedData) {
                    const extracted = contextDef.extract(resolvedData);
                    if (extracted) {
                        context[contextDef.key] = extracted;
                    }
                }
            } catch (e) {
                // Resolved data not available for this context type
                continue;
            }
        }

        return context;
    }

    /**
     * Determine smart fallback destination based on available context
     * Uses existing contextDefinitions and their priorities to intelligently
     * choose a return destination when no history exists
     */
    private determineSmartFallback(): { stateName: string; params: any } {
        const currentContext = this.extractContextFromCurrentState();
        const currentStateName = this.stateService.current.name;

        // Sort by existing priority (highest first) - reuses PRIORITY_* constants
        const sortedDefs = [...this.contextDefinitions].sort((a, b) => b.priority - a.priority);

        // Try each context type in priority order
        for (const contextDef of sortedDefs) {
            const contextData = currentContext[contextDef.key];
            if (!contextData) {
                continue;
            }

            // Find the fallback mapping for this context type
            const mapping = this.contextFallbackMappings.find(m => m.contextKey === contextDef.key);
            if (!mapping) {
                continue;
            }

            // Check if we should use this context
            if (mapping.shouldUse(contextData)) {
                return mapping.getDestination(contextData, currentStateName);
            }
        }

        // No context available or applicable - infer My Items tab from current state
        // If current state involves requests, return to requests tab; otherwise compliance tab
        const targetState = currentStateName?.includes('request')
            ? 'authenticated.my-items.requests'
            : 'authenticated.my-items.compliance';

        return { stateName: targetState, params: {} };
    }

    /**
     * Get the full history for debugging purposes
     */
    getHistory(): NavigationEntry[] {
        return [...this.history];
    }

    /**
     * Clear the navigation history
     * Useful when logging out or resetting the application state
     */
    clearHistory() {
        this.history = [];
    }
}
